/* eslint-disable @typescript-eslint/only-throw-error */
import { type LoaderFunction, redirect, replace } from 'react-router'
import { Forbidden, NotFound } from 'fejl'
import { RpcError, StatusCode } from 'grpc-web'
import type { CreateJobRoutesOptions } from '@/features/jobs/routes'
import {
  isJobShortId,
  JobShortIdLookupError,
  JobShortIdNotFoundError,
} from '@/features/jobs/job-short-id'
import type { JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { links } from '@/utils/link'
import { Id } from '@/infra/id'
import { PrefilledDraftInput } from '@/features/jobs/drafting/PrefilledDraftInput'
import { Job } from '@/features/jobs/Job'
import type { InternalJobsFeature } from '@/features/jobs/internal'

/**
 * Data returned by {@link createJobDetailsViewLoader}. `null` when no
 * workspace is selected yet, so the authentication screen is showing.
 */
export type JobDetailsViewLoaderData =
  { vm: JobDetailsViewModel } | { denied: string } | null

/**
 * Creates a loader function for the index route that sends firm admins who may
 * view the firm dashboard there, and everyone else to opportunities.
 *
 * @param options
 */
export function createDashboardViewLoader(
  options: CreateJobRoutesOptions,
): LoaderFunction {
  return async ({ request }) => {
    const { workspace } = await options.waitForAuthResolution(request.signal)
    if (!workspace) {
      return null
    }

    const internal = await options.jobs.internal()
    throw redirect(
      internal.jobRootViewModel.isFirmAdmin &&
        internal.firmDashboardViewModel.canViewFirmDashboard
        ? links.firmDashboard()
        : links.opportunities(),
    )
  }
}

/**
 * Creates a loader function for the firm dashboard that redirects home
 * without access.
 *
 * @param options
 */
export function createFirmDashboardViewLoader(
  options: CreateJobRoutesOptions,
): LoaderFunction {
  return async ({ request }) => {
    const { workspace } = await options.waitForAuthResolution(request.signal)
    if (!workspace) {
      return null
    }

    const { firmDashboardViewModel } = await options.jobs.internal()
    if (!firmDashboardViewModel.canViewFirmDashboard) {
      throw redirect(links.welcome())
    }

    return null
  }
}

/**
 * Creates a loader function for the job details view that fetches the job
 * before the view renders, and redirects short IDs to the canonical URL.
 *
 * @param options
 * @param buildPath Builds the canonical path for a job ID.
 */
export function createJobDetailsViewLoader(
  options: CreateJobRoutesOptions,
  buildPath: (jobId: string) => string = links.job,
): LoaderFunction {
  return async ({ params, request }) => {
    const { workspace } = await options.waitForAuthResolution(request.signal)
    if (!workspace || !params.jobId) {
      return null
    }

    const internal = await options.jobs.internal()

    if (isJobShortId(params.jobId)) {
      return redirectFromShortId(
        internal,
        params.jobId,
        buildPath,
        new URL(request.url),
      )
    }

    try {
      const vm = await acquireJobDetailsViewModel(
        internal,
        Id.assert(params.jobId),
        request.signal,
      )
      return { vm }
    } catch (err) {
      if (isNotFoundError(err)) {
        throw new Response(null, { status: 404 })
      }

      // Shown in the page, so the navigation stays usable.
      if (isForbiddenError(err)) {
        return { denied: err.message }
      }

      throw err
    }
  }
}

/**
 * Data returned by {@link createJobPaneLoader}. `null` when no job is
 * selected, or when no workspace is selected yet.
 */
export type JobPaneLoaderData =
  { vm: JobDetailsViewModel } | { denied: string } | { error: unknown } | null

/**
 * Creates a loader function for a page of another feature that shows the
 * route's job in a pane. A job that fails to load is shown in the pane, so
 * the rest of the page stays usable.
 *
 * @param options
 */
export function createJobPaneLoader(
  options: CreateJobRoutesOptions,
): LoaderFunction {
  return async ({ params, request }): Promise<JobPaneLoaderData> => {
    const { workspace } = await options.waitForAuthResolution(request.signal)
    if (!workspace || !params.jobId) {
      return null
    }

    const internal = await options.jobs.internal()
    try {
      const vm = await acquireJobDetailsViewModel(
        internal,
        Id.assert(params.jobId),
        request.signal,
      )
      return { vm }
    } catch (err) {
      if (isForbiddenError(err)) {
        return { denied: err.message }
      }

      return { error: err }
    }
  }
}

async function acquireJobDetailsViewModel(
  internal: ReturnType<typeof InternalJobsFeature>,
  id: Id,
  signal: AbortSignal,
) {
  const job = await internal.jobStore.fetchJob(id, undefined, signal)
  const vm = await internal.acquireJobDetailsViewModel(job, signal)
  // The job view is usable without what this loads, so switching jobs
  // doesn't wait on its requests.
  void vm.fetch(signal)
  return vm
}

async function redirectFromShortId(
  internal: ReturnType<typeof InternalJobsFeature>,
  shortId: string,
  buildPath: (jobId: string) => string,
  url: URL,
): Promise<never> {
  let jobId: Id
  try {
    jobId = await internal.searchableJobStore.findJobIdByShortId(shortId)
  } catch (err) {
    throw new JobShortIdLookupError(err)
  }
  if (!jobId) {
    throw new JobShortIdNotFoundError(shortId)
  }

  throw redirect(buildPath(jobId) + url.search + documentHashFor(url))
}

/**
 * The router strips the hash from loader requests. A short-ID link arrives
 * as a direct load, where the document URL still carries it.
 */
function documentHashFor(url: URL) {
  const current = new URL(window.location.href)
  return current.pathname === url.pathname && current.search === url.search
    ? current.hash
    : ''
}

function isNotFoundError(err: unknown) {
  return (
    err instanceof NotFound ||
    (err instanceof RpcError && err.code === StatusCode.NOT_FOUND)
  )
}

function isForbiddenError(err: unknown): err is Error {
  return (
    err instanceof Forbidden ||
    (err instanceof RpcError && err.code === StatusCode.PERMISSION_DENIED)
  )
}

/**
 * Creates a loader function for the checkout view that fetches the checkout
 * info for the route's job, or for the `job_ids` in the query string, and
 * redirects short IDs to the canonical URL.
 *
 * @param options
 */
export function createCheckoutViewLoader(
  options: CreateJobRoutesOptions,
): LoaderFunction {
  return async ({ params, request }) => {
    const { workspace } = await options.waitForAuthResolution(request.signal)
    if (!workspace) {
      return null
    }

    const internal = await options.jobs.internal()
    const url = new URL(request.url)

    if (params.jobId && isJobShortId(params.jobId)) {
      return redirectFromShortId(internal, params.jobId, links.checkout, url)
    }

    const jobIds = params.jobId
      ? [params.jobId]
      : (url.searchParams.get('job_ids') ?? '').split(',').filter(Boolean)
    const queryJobIds = url.searchParams.get('job_ids')
    const getLinkWithoutJob = queryJobIds
      ? (jobId: Id) => {
          const search = new URLSearchParams(url.search)
          search.set(
            'job_ids',
            queryJobIds
              .split(',')
              .filter((id) => id !== jobId)
              .join(','),
          )
          return `${url.pathname}?${search.toString()}`
        }
      : null
    // The payment section shows a failed fetch with the task's own state.
    await internal.checkoutVm
      .fetch({
        jobIds: jobIds.map((id) => Id.assert(id)),
        getLinkWithoutJob,
        signal: request.signal,
      })
      .catch(() => undefined)

    return {}
  }
}

/**
 * Creates a loader function for the draft editor that loads the route's draft,
 * or prepares a new one prefilled from the query string. Redirects to the job
 * when it is not a draft, to the job list when the draft does not exist, and
 * to the draft's own URL when preparing a new one created it.
 *
 * @param options
 */
export function createDraftJobViewLoader(
  options: CreateJobRoutesOptions,
): LoaderFunction {
  return async ({ params, request }) => {
    const { workspace } = await options.waitForAuthResolution(request.signal)
    if (!workspace) {
      return null
    }

    const { jobCreationRootVm, jobStore } = await options.jobs.internal()
    const jobId = params.jobId ? Id.assert(params.jobId) : Id.none
    // The editor already holds a draft it just created, so only check others.
    if (Id.some(jobId) && jobCreationRootVm.current.job.id !== jobId) {
      const existing = await jobStore
        .fetchJob(jobId, undefined, request.signal)
        .catch((err: unknown) => {
          if (isNotFoundError(err)) {
            throw replace(links.jobs())
          }
          request.signal.throwIfAborted()
          // The editor's own fetch surfaces this in the view.
          return null
        })

      // Preparing an editor for a submitted job never settles.
      if (existing && !existing.isDraft) {
        throw replace(links.job(existing.id))
      }
    }

    try {
      await jobCreationRootVm.edit(
        jobId,
        PrefilledDraftInput.parse(new URL(request.url).search),
      )
    } catch (err) {
      if (isNotFoundError(err)) {
        throw replace(links.jobs())
      }
      // The view renders a failed edit with the task's own state.
    }

    // The root only follows the editor's redirect streams while the view is
    // mounted, which it is not yet on a direct load.
    const { job } = jobCreationRootVm.current
    if (
      !Id.some(jobId) &&
      jobCreationRootVm.edit.resolved &&
      job !== Job.none
    ) {
      throw replace(links.editDraftJob(job.id))
    }

    return null
  }
}

/**
 * Creates a loader function for the job list that answers the offer named by
 * `acceptOffer` / `declineOffer` in the query string, then drops them from
 * the URL.
 *
 * @param options
 */
export function createJobListViewLoader(
  options: CreateJobRoutesOptions,
): LoaderFunction {
  return async ({ request }) => {
    const { workspace } = await options.waitForAuthResolution(request.signal)
    if (!workspace) {
      return null
    }

    const internal = await options.jobs.internal()
    const url = new URL(request.url)
    const search =
      internal.jobManagementViewModel.respondToOfferInQuery(url.search) ??
      internal.jobManagementViewModel.openSharedTabInQuery(url.search)
    if (search !== null) {
      return replace(search ? `${url.pathname}?${search}` : url.pathname)
    }

    return {}
  }
}

/**
 * Creates a loader function for the scoping jobs view that loads the route's
 * scoping job. Redirects home without access, and to the list when the
 * scoping job does not exist or is completed.
 *
 * @param options
 */
export function createScopingJobsViewLoader(
  options: CreateJobRoutesOptions,
): LoaderFunction {
  return async ({ params, request }) => {
    const { workspace } = await options.waitForAuthResolution(request.signal)
    if (!workspace) {
      return null
    }

    const { ocrScopingJobsViewModel } = await options.jobs.internal()
    if (!ocrScopingJobsViewModel.permissionVm.canViewScopingJobs) {
      throw redirect(links.welcome())
    }

    // The view renders a failed load with the task's own state.
    const scopeWork = await ocrScopingJobsViewModel.mainPanelVm
      .fetch(Id(params.scopeWorkId), request.signal)
      .catch(() => undefined)
    if (scopeWork === null) {
      throw redirect(links.scopingJobs())
    }

    return null
  }
}
